/**
 * The renewals screen's two formatters.
 *
 * A separate module rather than exports on the page, for the reason `react-refresh` states
 * in the lint rule: a file that exports both components and constants breaks fast refresh.
 * The repo's baseline is 31 warnings and this feature is not entitled to raise it.
 */

import { NO_VALUE, formatDateLong, formatTime } from '../inquiries/format';

/**
 * "22 Aug 2026" for a DATE-ONLY value.
 *
 * ⚠ NOT `formatDateLong`, AND THE DIFFERENCE IS A WHOLE DAY. That helper does
 * `new Date(iso)`, which reads a bare 'YYYY-MM-DD' as UTC MIDNIGHT, and then formats it in
 * the browser's LOCAL time — so for any viewer west of Greenwich, `2026-08-22` renders as
 * "21 Aug 2026". It is correct for the timestamps it was written for (`created_at` carries a
 * real instant) and wrong for every column on the renewals screen, all of which are calendar
 * dates with no time in them at all.
 *
 * Formatting in UTC is what makes the rendered day the stored day. On a screen whose entire
 * job is telling somebody when a contract ends, a day is not a rounding error.
 */
export function formatDateOnly(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return NO_VALUE;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  if (!Number.isFinite(parsed.getTime())) return NO_VALUE;
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(parsed);
}

/**
 * "22 Aug 2026, 14:05" for a real INSTANT — when a reminder actually went out.
 *
 * ⚠ THE OPPOSITE RULE TO `formatDateOnly` ABOVE, AND DELIBERATELY SO. `expiry_date` is a
 * calendar date with no time in it, so rendering it in local time loses a day. `sent_at` is
 * a moment: somebody pressed a button at a point in time, and the honest rendering of that
 * is the reader's own clock — "did a colleague send this five minutes ago" is the question
 * it answers, and answering it in a country's timezone the reader is not in would be worse
 * than useless. So this one uses the shared instant formatters unchanged.
 */
export function formatSentAt(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return NO_VALUE;
  const day = formatDateLong(iso);
  if (day === NO_VALUE) return NO_VALUE;
  return `${day}, ${formatTime(iso)}`;
}

/**
 * "in 21 days" / "today" / "9 days ago".
 *
 * The number is the SERVER's `days_remaining`, computed in SQL against the COUNTRY's
 * calendar day. This function only words it — it never recomputes from a clock, because the
 * browser's day and the country's day are not the same day for several hours out of every
 * twenty-four.
 */
export function daysLabel(days: number): string {
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days > 0) return `in ${String(days)} days`;
  const overdue = Math.abs(days);
  return overdue === 1 ? '1 day ago' : `${String(overdue)} days ago`;
}
